import React, { useEffect, useState } from 'react';
import { ArrowRight, Calendar, Heart, LogIn, MapPin, Search, UserPlus, Users } from 'lucide-react';
import { login, register } from '../api';
import type { User } from '../types';

interface LandingProps {
  page: 'landing' | 'login' | 'register';
  onAuthenticated: (user: Omit<User, 'passwordHash'>) => void;
  onNavigate: (path: string) => void;
}

const features = [
  {
    icon: Users,
    title: 'Guest List Control',
    description: 'Keep every guest, table assignment, and event detail organized in one place.',
  },
  {
    icon: MapPin,
    title: 'Visual Floor Plans',
    description: 'Map the room visually so guests can understand the layout the moment they arrive.',
  },
  {
    icon: Search,
    title: 'Instant Seat Lookup',
    description: 'Give guests a simple search page where they can find their table in seconds.',
  },
  {
    icon: Calendar,
    title: 'Multiple Event Types',
    description: 'Use it for weddings, galas, conferences, fundraisers, and private celebrations.',
  },
];

export default function Landing({ page, onAuthenticated, onNavigate }: LandingProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setError('');
  }, [page]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login({ email, password });
      if (response.success && response.user) {
        onAuthenticated(response.user);
      } else {
        setError(response.error || 'Login failed');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await register({ email, password, name });
      if (response.success && response.user) {
        onAuthenticated(response.user);
      } else {
        setError(response.error || 'Registration failed');
      }
    } catch (err) {
      setError('An error occurred during registration');
    } finally {
      setLoading(false);
    }
  };

  const isLogin = page === 'login';

  const renderAuthPanel = () => {
    if (page === 'landing') {
      return (
        <div className="auth-panel card">
          <span className="eyebrow">SeatFinder.app</span>
          <h2>Make guest arrivals feel calm and effortless.</h2>
          <p className="auth-panel-copy">
            Create the event, assign tables, and share a simple guest search page so nobody is stuck asking where to sit.
          </p>

          <div className="auth-cta-list">
            <button className="btn" onClick={() => onNavigate('/login')}>
              <LogIn size={18} />
              Sign In
            </button>
            <button className="btn btn-secondary" onClick={() => onNavigate('/register')}>
              <UserPlus size={18} />
              Create Account
            </button>
          </div>

          <div className="route-preview">
            <div className="route-preview__row">
              <span>Create</span>
              <span>Set up your event, date, and guest list.</span>
            </div>
            <div className="route-preview__row">
              <span>Design</span>
              <span>Arrange your floor plan and seating layout visually.</span>
            </div>
            <div className="route-preview__row">
              <span>Share</span>
              <span>Let guests search their name and find the right table fast.</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="auth-panel card">
        <span className="eyebrow">{isLogin ? 'Owner Access' : 'Create Account'}</span>
        <h2>{isLogin ? 'Sign in to manage your events.' : 'Create an account to manage your events.'}</h2>
        <p className="auth-panel-copy">
          {isLogin
            ? 'Use your account to open the dashboard, manage guests, and edit floor plans.'
            : 'Start with a new account, then create events and share guest-facing search pages.'}
        </p>

        <form onSubmit={isLogin ? handleLogin : handleRegister} className="auth-form">
          {!isLogin && (
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                id="name"
                type="text"
                className="login-input"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="login-input"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              className="login-input"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={isLogin ? undefined : 6}
            />
            {!isLogin && <small className="form-hint">Minimum 6 characters</small>}
          </div>

          {error && <div className="error-box">{error}</div>}

          <button type="submit" className="btn" disabled={loading}>
            {loading ? (isLogin ? 'Signing in...' : 'Creating account...') : (isLogin ? 'Sign In' : 'Create Account')}
          </button>

          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onNavigate(isLogin ? '/register' : '/login')}
          >
            {isLogin ? 'Create an Account' : 'Already have an account?'}
          </button>

          <button
            type="button"
            className="btn btn-tertiary"
            onClick={() => {
              setError('');
              setPassword('');
              if (!isLogin) {
                setName('');
              }
              onNavigate('/');
            }}
          >
            Back to Home
          </button>
        </form>
      </div>
    );
  };

  return (
    <div className="auth-layout">
      <section className="auth-showcase">
        <div className="brand-lockup">
          <div className="brand-mark">
            <Heart size={18} />
            seatfinder.app
          </div>
          <span className="eyebrow">Elegant seating made simple</span>
        </div>

        <div className="auth-copy">
          <h1>Help every guest find their seat without the chaos.</h1>
          <p>
            SeatFinder.app gives event organizers a polished way to manage guest lists, build seating layouts, and share a beautiful lookup page guests can use on their phone.
          </p>
        </div>

        <div className="feature-grid">
          {features.map(({ icon: Icon, title, description }) => (
            <article key={title} className="feature-card">
              <div className="feature-icon">
                <Icon size={20} />
              </div>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>

        <div className="showcase-note">
          <div className="showcase-note__title">Built for the moments that matter</div>
          <p>From weddings to formal dinners, SeatFinder.app keeps check-in smooth and helps guests feel guided from the moment they arrive.</p>
          <button className="link-button" onClick={() => onNavigate('/login')}>
            Already have an account? Sign in
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      <aside className="auth-sidebar">
        {renderAuthPanel()}
      </aside>
    </div>
  );
}
